/**
 * Stale-bundle recovery (#47038): a hydrated session that outlives its deploy
 * keeps referencing hashed chunks the server no longer serves. Vite surfaces
 * that as an unhandled dynamic-import rejection (or a failed script/css
 * resource load), which otherwise dies silently mid-interaction or white-screens
 * the next route change. This installs one module-level listener pair and
 * renders an explicit reload affordance instead.
 *
 * Installed once per document from verification/shell.tsx at module scope, so
 * every route that mounts the product chrome carries it — including the first
 * paint, before any chunk can fail.
 */

import { reloadCurrentDocument } from "../shared/reload-current-document";

const CHUNK_LOAD_FAILURE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script/i;

function isChunkLoadFailure(reason: unknown): boolean {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : "";
  return CHUNK_LOAD_FAILURE.test(message);
}

export function installStaleAppRecovery(): void {
  if (typeof window === "undefined") return;
  const w = window as typeof window & { __staleAppRecoveryInstalled?: boolean };
  if (w.__staleAppRecoveryInstalled) return;
  w.__staleAppRecoveryInstalled = true;

  const show = () => {
    if (document.querySelector('[data-stale-app-recovery="visible"]')) return;
    const banner = document.createElement("div");
    banner.setAttribute("data-stale-app-recovery", "visible");
    banner.setAttribute("role", "alert");
    const s = banner.style;
    s.position = "fixed";
    s.left = "50%";
    s.bottom = "calc(var(--spacing) * 6)";
    s.transform = "translateX(-50%)";
    s.zIndex = "2147483647";
    s.display = "flex";
    s.alignItems = "center";
    s.gap = "calc(var(--spacing) * 3)";
    s.padding = "calc(var(--spacing) * 2.5) calc(var(--spacing) * 3.5)";
    // radius-ignore: imperative stale-app recovery banner is outside the React/Tailwind render tree
    s.borderRadius = "10px";
    s.background = "#1c1c1c";
    s.color = "#ffffff";
    s.font = "500 var(--type-body-size)/var(--type-body-leading) var(--font-sans)";
    s.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.25)";
    const label = document.createElement("span");
    label.textContent = "App updated — reload";
    const reload = document.createElement("button");
    reload.type = "button";
    reload.textContent = "Reload";
    const rs = reload.style;
    rs.cursor = "pointer";
    rs.padding = "calc(var(--spacing) * 1.5) calc(var(--spacing) * 3)";
    // radius-ignore: imperative stale-app recovery banner is outside the React/Tailwind render tree
    rs.borderRadius = "8px";
    rs.border = "1px solid rgba(255, 255, 255, 0.35)";
    rs.background = "#ffffff";
    rs.color = "#111111";
    rs.font = "inherit";
    reload.addEventListener("click", reloadCurrentDocument);
    banner.append(label, reload);
    document.body.appendChild(banner);
  };

  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadFailure(event.reason)) return;
    event.preventDefault();
    show();
  });
  window.addEventListener(
    "error",
    (event) => {
      const target = event.target;
      if (
        target instanceof HTMLScriptElement ||
        (target instanceof HTMLLinkElement && target.relList.contains("stylesheet"))
      ) {
        show();
      }
    },
    true,
  );
}
